import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useVoiceDictation
 *
 * Thin wrapper around the browser Web Speech API. Emits transcripts via
 * `onTranscript` and exposes start/stop controls. Auto-restarts the
 * recognizer if the browser silently ends the session (Chrome does this
 * after ~30s of silence even in continuous mode).
 *
 * Why interimResults + debounce + restart-on-commit?
 *   The browser only fires `isFinal=true` after ~700–1000ms of silence,
 *   which felt sluggish for single-digit dictation. With interim results
 *   we can detect a pause much sooner (default 500ms) and commit the
 *   current transcript ourselves, then restart the recognizer so the next
 *   utterance starts cleanly without inheriting prefix text.
 *
 * Usage:
 *   const { isSupported, isListening, start, stop } = useVoiceDictation({
 *     onTranscript: (text) => ...,
 *     onError:      (e) => ...,
 *     commitDelayMs: 500, // optional; how long of a pause counts as "done"
 *   });
 */
export function useVoiceDictation({ onTranscript, onError, commitDelayMs = 500 } = {}) {
  const [isListening, setIsListening] = useState(false);

  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  const commitDelayRef = useRef(commitDelayMs);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { commitDelayRef.current = commitDelayMs; }, [commitDelayMs]);

  const SR = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const isSupported = !!SR;

  // recognitionRef holds the *active* instance. wantListeningRef tracks the
  // user's intent (true between start() and stop()). The two are decoupled
  // so onend can decide whether to restart automatically.
  const recognitionRef = useRef(null);
  const wantListeningRef = useRef(false);

  // Per-utterance state, recreated on every start(). Holding it in a ref so
  // the onresult handler can be defined inside start() without re-binding.
  const utterRef = useRef({ text: "", idx: -1, consumed: new Set(), timer: null });

  const clearUtter = () => {
    const u = utterRef.current;
    if (u.timer) clearTimeout(u.timer);
    u.text = "";
    u.idx = -1;
    u.timer = null;
  };

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    clearUtter();
    utterRef.current.consumed = new Set();
    if (rec) {
      try { rec.stop(); } catch (_) { /* ignore */ }
    }
    setIsListening(false);
  }, []);

  const start = useCallback(() => {
    if (!isSupported) return false;
    // If already running, no-op.
    if (recognitionRef.current) return true;

    const rec = new SR();
    rec.lang = "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    // Reset per-session state.
    clearUtter();
    utterRef.current.consumed = new Set();

    rec.onstart = () => setIsListening(true);

    // Emit the buffered interim transcript and request a fresh utterance
    // by stopping the recognizer (onend will restart it automatically).
    const commitAndRestart = () => {
      const u = utterRef.current;
      if (u.timer) { clearTimeout(u.timer); u.timer = null; }
      if (u.idx === -1 || u.consumed.has(u.idx) || !u.text) {
        u.text = ""; u.idx = -1;
        return;
      }
      const txt = u.text.trim();
      u.consumed.add(u.idx);
      u.text = ""; u.idx = -1;
      if (txt && onTranscriptRef.current) {
        try { onTranscriptRef.current(txt); } catch (_) { /* ignore consumer errors */ }
      }
      // Force a clean utterance boundary so the recognizer doesn't keep
      // appending future words to the same already-emitted result.
      try { rec.stop(); } catch (_) { /* ignore */ }
    };

    rec.onresult = (e) => {
      // Walk the new results starting at resultIndex. We track the latest
      // non-consumed result and let the debounce / final-flag drive commit.
      const u = utterRef.current;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (u.consumed.has(i)) continue;
        const r = e.results[i];
        const txt = (r[0]?.transcript || "").trim();
        if (!txt) continue;

        // If this is a new result index, drop any pending debounce on the
        // previous one — that previous utterance is no longer the active one.
        if (u.idx !== i) {
          if (u.timer) { clearTimeout(u.timer); u.timer = null; }
          u.idx = i;
        }
        u.text = txt;

        if (r.isFinal) {
          // Browser-confirmed boundary — commit immediately, no restart needed
          // (the browser already opened a new result index).
          if (u.timer) { clearTimeout(u.timer); u.timer = null; }
          u.consumed.add(i);
          u.text = ""; u.idx = -1;
          if (onTranscriptRef.current) {
            try { onTranscriptRef.current(txt); } catch (_) { /* ignore */ }
          }
        } else {
          // Interim — start/refresh debounce. When it fires we'll force a
          // commit + restart.
          if (u.timer) clearTimeout(u.timer);
          u.timer = setTimeout(commitAndRestart, commitDelayRef.current);
        }
      }
    };

    rec.onerror = (e) => {
      if (onErrorRef.current) onErrorRef.current(e);
      if (e.error === "not-allowed" || e.error === "service-not-allowed" || e.error === "audio-capture") {
        wantListeningRef.current = false;
        recognitionRef.current = null;
        setIsListening(false);
      }
    };

    rec.onend = () => {
      // Reset per-utterance state so the next session starts clean.
      clearUtter();
      utterRef.current.consumed = new Set();
      // Restart if we are still the active recognizer AND user wants listening.
      if (recognitionRef.current === rec && wantListeningRef.current) {
        try {
          rec.start();
        } catch (_) {
          recognitionRef.current = null;
          setIsListening(false);
        }
      } else {
        setIsListening(false);
      }
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      wantListeningRef.current = true;
      return true;
    } catch (err) {
      recognitionRef.current = null;
      if (onErrorRef.current) onErrorRef.current({ error: "start-failed", message: err?.message });
      return false;
    }
  }, [SR, isSupported]);

  // Cleanup on unmount.
  useEffect(() => () => { stop(); }, [stop]);

  return { isSupported, isListening, start, stop };
}
